import type { ReactNode } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '../../utils/cn'
import type { TestIdProps } from '../internal/test-id'

/**
 * Loading placeholders, composed rather than configured.
 *
 *   <SkeletonArea label="Loading invoices">
 *       <SkeletonRow>
 *           <Skeleton shape="circle" />
 *           <Skeleton shape="heading" width="half" />
 *       </SkeletonRow>
 *       <Skeleton />
 *       <Skeleton width="half" />
 *   </SkeletonArea>
 *
 * The donors went the other way: three products each shipped a
 * BlockSkeleton whose only variant was ten hardcoded pixel heights, plus a
 * TextSkeleton keyed by heading level, plus SkeletonAvatar, SkeletonButton,
 * SkeletonTitle, SkeletonCell, SkeletonTable, SkeletonPage. Every new layout
 * needed a new component. Three shapes and two containers cover all of them.
 *
 * None of the donors announced anything. A screen reader met a dozen grey
 * boxes and either read nothing or read the page as empty, so the user could
 * not tell a slow load from a broken one. Here the shapes are `aria-hidden`
 * and SkeletonArea does the announcing.
 */
const skeletonVariants = cva('bg-background-selected block animate-pulse motion-reduce:animate-none', {
    variants: {
        shape: {
            text: 'h-4 rounded-aria-sm',
            heading: 'h-7 rounded-aria-sm',
            block: 'h-24 rounded-aria-md',
            circle: 'size-8 shrink-0 rounded-full'
        },
        width: {
            full: 'w-full',
            half: 'w-1/2',
            quarter: 'w-1/4'
        }
    },
    defaultVariants: { shape: 'text' }
})

/** A circle sizes itself, so offering it a width would only let a caller break it. */
export type SkeletonProps =
    { shape?: 'text' | 'heading' | 'block'; width?: 'full' | 'half' | 'quarter' } | { shape: 'circle'; width?: never }

export const Skeleton = ({ shape = 'text', width, 'data-testid': testId }: SkeletonProps & TestIdProps) => (
    <span
        data-testid={testId}
        aria-hidden
        className={cn(skeletonVariants({ shape, width: shape === 'circle' ? undefined : (width ?? 'full') }))}
    />
)

/** Places shapes side by side, e.g. an avatar next to a name. */
export const SkeletonRow = ({ children }: { children: ReactNode }) => (
    <div className="flex w-full items-center gap-3">{children}</div>
)

/**
 * The one thing assistive technology hears while the shapes are on screen.
 *
 * `label` is required for the same reason Spinner's is: an unnamed busy region
 * is announced as nothing.
 */
export const SkeletonArea = ({
    label,
    children,
    'data-testid': testId
}: { label: string; children: ReactNode } & TestIdProps) => (
    <div role="status" aria-busy="true" data-testid={testId} className="flex w-full flex-col gap-3">
        <span className="sr-only">{label}</span>
        {children}
    </div>
)
